import React, { Suspense, useState, useRef, useEffect, useMemo } from 'react'
import { Canvas, useFrame, useLoader, useUpdate, useThree } from 'react-three-fiber'
import { HTML } from 'drei'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { OrbitControls } from 'drei'
import { useElementOnScreen } from './useElementOnScreen'
import { useScreenSize, deviceMap } from './screenSize'
import SafeModelViewer from './SafeModelViewer'
import { useViewportVisibility } from './useViewportVisibility'
import '@google/model-viewer'

const COWS = 'https://jake.surf/assets/cows'
const CERAMICS = 'https://jake.surf/assets/ceramics'

const appaloosaimg = `${COWS}/appaloosa1.jpg`
const appaloosa1img = `${COWS}/appaloosa.jpg`
const appaloosaglb = `${COWS}/appaloosa.glb`
const bluecowsgrazingimg = `${COWS}/bluecowsgrazing.jpg`
const cowwithpoppiesimg = `${COWS}/cowwithpoppies.jpg`
const cowwithpoppiessunlightimg = `${COWS}/cowwithpoppiessunlight.jpg`
const shortflowers = `${COWS}/shortflowers.jpg`
const shortflowersglb = `${COWS}/shortflowersglb.glb`
const shortflowerssun = `${COWS}/shortflowersun.jpg`
const bluecowsgrazingglb = `${COWS}/poly.glb`
const bbcglb = `${COWS}/bbc.glb`
const bbcimg = `${COWS}/bbc.jpg`
const poppyfieldcow = `${COWS}/poppyfieldcow.glb`
const greencowimg = `${COWS}/greencow.jpg`
const greencowglb = `${COWS}/greencow.glb`
const horseimg1 = `${COWS}/horse1.jpg`
const horseglb = `${COWS}/horse.glb`
const icefieldcowsimg = `${COWS}/icefieldcows.jpg`
const icefieldcowsglb = `${COWS}/icefieldcows.glb`
const muscleimg = `${COWS}/muscle.jpg`
const muscleglb = `${COWS}/muscle.glb`
const pellegrinoimg = `${COWS}/pellegrino.jpg`
const pellegrinolb = `${COWS}/pellegrino.glb`
const swimmerimg = `${COWS}/swimmer.jpg`
const swimmerglb = `${COWS}/swimmer.glb`
const horsecowimg = `${COWS}/horsecow.jpg`
const horsecowglb = `${COWS}/horsecow.glb`
const tallflowersimg = `${COWS}/tallflowers.jpg`
const tallflowers1img = `${COWS}/tallflowers1.jpg`
const tallflowersglb = `${COWS}/tallflowers.glb`
const dogimg = `${COWS}/dog.jpg`
const dogglb = `${COWS}/dog.glb`
const bluetruckimg = `${COWS}/bluetruck.jpg`
const bluetruckglb = `${COWS}/bluetruck.glb`
const pog = `${COWS}/Portraits%20Over%20Gchat.jpg`
const pog1 = `${COWS}/Portraits%20Over%20Gchat%20Ch.%201.jpg`
const pog2 = `${COWS}/Portraits%20Over%20Gchat%20Ch.%202.jpg`
const pog3 = `${COWS}/Portraits%20Over%20Gchat%20Ch.%203.jpg`
const cp1img = `${COWS}/cp1.jpg`
const cp1glb = `${COWS}/cp1.glb`
const cp2img = `${COWS}/cp2.jpg`
const cp3img = `${COWS}/cp3.jpg`
const cp3glb = `${COWS}/cp3.glb`
const cp4glb = `${COWS}/cp4.glb`
const zebraimg = `${COWS}/zebra.jpg`

const bowlGlb = `${CERAMICS}/bowl.glb`
const bowlUsdz = `${CERAMICS}/bowl.usdz`
const rogerGreenRoundMugGlb = `${CERAMICS}/roger-green-round-mug.glb`
const rogerGreenRoundMugUsdz = `${CERAMICS}/roger-green-round-mug.usdz`
const b18WarpedMugGlb = `${CERAMICS}/b18-warped-mug.glb`
const b18WarpedMugUsdz = `${CERAMICS}/b18-warped-mug.usdz`
const perfectBowlGlb = `${CERAMICS}/perfect-bowl.glb`
const perfectBowlUsdz = `${CERAMICS}/perfect-bowl.usdz`
const pillBowlGlb = `${CERAMICS}/pill-bowl.glb`
const pillBowlUsdz = `${CERAMICS}/pill-bowl.usdz`
const blueBowlGlb = `${CERAMICS}/blue-bowl.glb`
const blueBowlUsdz = `${CERAMICS}/blue-bowl.usdz`
const whiteSkybox = `${CERAMICS}/bowl.png`


const ModelViewer = (props) => <model-viewer {...props}></model-viewer>

function useKeypress(key, action) {
  useEffect(() => {
    function onKeyup(e) {
      if (e.key === key) action()
    }
    window.addEventListener('keydown', onKeyup)
    return () => window.removeEventListener('keydown', onKeyup)
  })
}

const GLTFImage = ({ glb, vertCounter, horizCounter, rotation }) => {
  const [gltf, set] = useState()
  useEffect(() => void new GLTFLoader().load(glb, set), [])

  if (!gltf) {
    return (
      <HTML>
        <div style={{ marginLeft: -120 }}>Loading... (big file, may take several seconds)</div>
      </HTML>
    )
  }

  return <primitive object={gltf.scene} scale={[1, 1, 1]} position={[vertCounter, horizCounter, 0]} rotation={rotation} />
}

const ImageRow = ({ glb, rotation, marginLeft, marginTop }) => {
  const virtualCamera = useRef()
  const [vertCounter, setVertCounter] = useState(0)
  const [horizCounter, setHorizCounter] = useState(0)
  const incrementValue = 0.05

  const [letterClicked, setLetterClicked] = useState('')
  const isLetterClicked = (letter) => letterClicked === letter
  const letterClickedColor = (letter) => {
    if (letterClicked === '') {
      return 'black'
    }

    return isLetterClicked(letter) ? 'blue' : 'black'
  }
  const letterClickedBorder = (letter) => letterClicked !== '' && isLetterClicked(letter)
  const activatedBorder = '2px solid blue'
  const activatedBackgroundColor = 'lightblue'

  useKeypress('w', () => {
    setHorizCounter(horizCounter - incrementValue)
    setLetterClicked('w')
  })
  useKeypress('a', () => {
    setVertCounter(vertCounter + incrementValue)
    setLetterClicked('a')
  })
  useKeypress('d', () => {
    setVertCounter(vertCounter - incrementValue)
    setLetterClicked('d')
  })
  useKeypress('s', () => {
    setHorizCounter(horizCounter + incrementValue)
    setLetterClicked('s')
  })

  const NSButtonSpacingOpts = { paddingLeft: 0, paddingRight: 0, width: 33, marginLeft: 38 }

  const activatedButton = {
    border: activatedBorder,
    backgroundColor: activatedBackgroundColor
  }

  return (
    <Suspense fallback={null}>
      <Canvas colorManagement camera={virtualCamera}>
        {/** shadows above is not a boolean value. should be shadows={0.73} */}
        {/* <directionalLight receiveShadow shadow-bias={-0.0001} position={[0, 0, 4]} intensity={3} color="orange" castShadow /> */}
        <ambientLight />
        <Suspense
          fallback={
            <HTML>
              <div style={{ marginLeft: -120 }}>Loading... (big file, may take several seconds)</div>
            </HTML>
          }>
          <OrbitControls camera={virtualCamera.current} enablePan={true} enableZoom={true} enableRotate={true} />
          <GLTFImage glb={glb} vertCounter={vertCounter} horizCounter={horizCounter} rotation={rotation} />
          <HTML style={{ marginTop, marginLeft }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <button
                style={{
                  color: letterClickedColor('w'),
                  ...(letterClickedBorder('w') ? activatedButton : {}),
                  marginBottom: 5,
                  ...NSButtonSpacingOpts
                }}
                onClick={() => {
                  setHorizCounter(horizCounter - incrementValue)
                  setLetterClicked('w')
                }}>
                W
              </button>
              <div style={{ marginBottom: 10, display: 'flex' }}>
                <button
                  style={{
                    color: letterClickedColor('a'),
                    ...(letterClickedBorder('a') ? activatedButton : {}),
                    marginRight: 5,
                    width: 33
                  }}
                  onClick={() => {
                    setVertCounter(vertCounter + incrementValue)
                    setLetterClicked('a')
                  }}>
                  A
                </button>
                <button
                  style={{
                    color: letterClickedColor('s'),
                    ...(letterClickedBorder('s') ? activatedButton : {}),
                    marginRight: 5,
                    width: 33
                  }}
                  onClick={() => {
                    setHorizCounter(horizCounter + incrementValue)
                    setLetterClicked('s')
                  }}>
                  S
                </button>
                <button
                  style={{ color: letterClickedColor('d'), ...(letterClickedBorder('d') ? activatedButton : {}), width: 33 }}
                  onClick={() => {
                    setVertCounter(vertCounter - incrementValue)
                    setLetterClicked('d')
                  }}>
                  D
                </button>
              </div>
              <button
                style={{ width: 80, marginLeft: 15 }}
                onClick={() => {
                  setVertCounter(0)
                  setHorizCounter(0)
                }}>
                Re-center
              </button>
            </div>
          </HTML>
        </Suspense>
      </Canvas>
    </Suspense>
  )
}

const DumbImage = ({ isMobile, imgSrc, glb, rotation, width, height, marginTop, marginLeft }) => {
  const [containerRef, isVisible] = useElementOnScreen({
    root: null,
    rootMargin: '0px',
    threshold: 0.5
  })

  // console.log({ containerRef, isVisible })
  if (Array.isArray(imgSrc)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {imgSrc.map((im) => (
            <img style={{ width, height }} src={im} alt="supp" />
          ))}
        </div>
        {!isMobile && glb && (
          <div style={{ width: '100%', minHeight: 800, height: 800 }} ref={containerRef}>
            {isVisible && <ImageRow glb={glb} rotation={rotation} marginLeft={marginLeft} marginTop={marginTop} />}
          </div>
        )}
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <img style={{ width, height }} src={imgSrc} alt="supp" />
      {!isMobile && glb && (
        <div style={{ width: '100%', minHeight: 800, height: 800 }} ref={containerRef}>
          {isVisible && <ImageRow glb={glb} rotation={rotation} marginLeft={marginLeft} marginTop={marginTop} />}
        </div>
      )}
    </div>
  )
}

const paintingConfig = [
  {
    imgSrc: bluecowsgrazingimg,
    glb: bluecowsgrazingglb,
    alt: 'Blue cows grazing',
    rotation: [2, 1.5, 4.1],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Blue cows grazing',
      year: '2020',
      dimensions: '24" x 36"',
      materials: 'oil on canvas',
      price: '$1400'
    }
  },
  {
    imgSrc: [cowwithpoppiesimg, cowwithpoppiessunlightimg],
    glb: poppyfieldcow,
    alt: 'Cow amongst poppies',
    rotation: [1.3, 1.53, 0.3],
    width: 608,
    height: 459,
    marginTop: -200,
    marginLeft: -400,
    details: {
      title: 'Cow among poppies',
      year: '2021',
      dimensions: '24" x 36" x 1/4"',
      materials: 'oil on plexiglass',
      price: '$2200'
    }
  },
  {
    imgSrc: [appaloosaimg, appaloosa1img],
    glb: appaloosaglb,
    alt: 'Appaloosa trot',
    rotation: [1.3, 1.53, 0.3],
    width: 608,
    height: 806,
    marginTop: -200,
    marginLeft: -400,
    details: {
      title: 'Appaloosa horse trot',
      year: '2022',
      dimensions: '16" x 16"',
      materials: 'oil on canvas',
      price: '$1100'
    }
  },
  {
    imgSrc: [shortflowers, shortflowerssun],
    glb: shortflowersglb,
    alt: 'Summer flowers',
    rotation: [1.3, 1.53, 0.3],
    width: 608,
    height: 806,
    marginTop: -200,
    marginLeft: -400,
    details: {
      title: 'Summer flowers',
      year: '2021',
      dimensions: '13" x 15" x 1/4"',
      materials: 'oil on plexiglass',
      price: '$500'
    }
  },
  {
    imgSrc: bbcimg,
    glb: bbcglb,
    alt: 'Big black cow',
    rotation: [-0.2, 1.53, 0.3],
    width: 608,
    height: 459,
    marginTop: -200,
    marginLeft: -400,
    details: {
      title: 'Big black cow',
      year: '2016',
      dimensions: '24" x 36"',
      materials: 'oil on canvas',
      price: '$6000'
    }
  },
  {
    imgSrc: icefieldcowsimg,
    glb: icefieldcowsglb,
    alt: 'Cows in an ice field',
    rotation: [2, 1.5, 4.1],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Cows in an ice field',
      year: '2016',
      dimensions: '20" x 16"',
      materials: 'oil on canvas',
      price: '$3200'
    }
  },
  {
    imgSrc: muscleimg,
    glb: muscleglb,
    alt: 'Wrestler',
    rotation: [1.4, 0, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Wrestler',
      year: '2021',
      dimensions: '13" x 15" x 1/4"',
      materials: 'oil on plexiglass',
      price: '$500'
    }
  },
  {
    imgSrc: pellegrinoimg,
    glb: pellegrinolb,
    alt: 'Flowers in pellegrino',
    rotation: [1.4, 1.5, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Flowers in pellegrino',
      year: '2021',
      dimensions: '13" x 15" x 1/4"',
      materials: 'oil on plexiglass',
      price: '$500'
    }
  },
  {
    imgSrc: swimmerimg,
    glb: swimmerglb,
    alt: 'Swimmer',
    rotation: [1.4, 1.5, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Swimmer',
      year: '2021',
      dimensions: '13" x 15" x 1/4"',
      materials: 'oil on plexiglass',
      price: '$500'
    }
  },
  {
    imgSrc: horseimg1,
    glb: horseglb,
    alt: 'Virginian horse',
    rotation: [-0.2, 1.53, 0.3],
    width: 608,
    height: 459,
    marginTop: -200,
    marginLeft: -400,
    details: {
      title: 'Virginian horse',
      year: '2020',
      dimensions: '30" x 15"',
      materials: 'oil on canvas',
      price: '$1200'
    }
  },
  {
    imgSrc: horsecowimg,
    glb: horsecowglb,
    alt: 'Horse cow',
    rotation: [0, 1.3, 0],
    width: 608,
    height: 806,
    marginTop: -200,
    marginLeft: -400,
    details: {
      title: 'Horse cow',
      year: '2021',
      dimensions: '13" x 15" x 1/8',
      materials: 'oil on plexiglass',
      price: '$500'
    }
  },
  {
    imgSrc: bluetruckimg,
    glb: bluetruckglb,
    alt: 'Cow jumping out of a truck',
    rotation: [0, 1.3, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Cow jumping out of a truck',
      year: '2018',
      dimensions: '24" x 18"',
      materials: 'oil on canvas'
    }
  },
  {
    imgSrc: greencowimg,
    glb: greencowglb,
    alt: 'Cow in daisy field',
    rotation: [2, 1.5, 4.1],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Cow in daisy field',
      year: '2018',
      dimensions: '14" x 11"',
      materials: 'oil on canvas',
      price: '$1000'
    }
  },
  {
    imgSrc: [tallflowersimg, tallflowers1img],
    glb: tallflowersglb,
    alt: 'Fall flowers',
    rotation: [0, 1.6, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Fall flowers',
      year: '2021',
      dimensions: '30" x 15" x 1/4"',
      materials: 'oil on plexiglass',
      price: '$750'
    }
  },
  {
    imgSrc: dogimg,
    glb: dogglb,
    alt: 'Fire hydrant',
    rotation: [1.5, 0, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Fire hydrant',
      year: '2014',
      dimensions: '12" x 12"',
      materials: 'oil on canvas'
    }
  }
]

const platesConfig = [
  {
    imgSrc: zebraimg,
    glb: null,
    alt: 'Zebra plate',
    rotation: [1.2, 2.7, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Zebra plate',
      year: '2021',
      dimensions: '10" x 10"',
      materials: 'ceramic plate'
    }
  },
  {
    imgSrc: cp1img,
    glb: cp1glb,
    alt: 'Cow plate 1',
    rotation: [1, 1.5, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Cow plate 1',
      year: '2020',
      dimensions: '10" x 10"',
      materials: 'ceramic plate',
      price: ''
    }
  },
  {
    imgSrc: cp2img,
    glb: cp3glb,
    alt: 'Cow plate 2',
    rotation: [1.3, 0.2, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Cow plate 2',
      year: '2019',
      dimensions: '10" x 10"',
      materials: 'ceramic plate'
    }
  },
  {
    imgSrc: cp3img,
    glb: cp4glb,
    alt: 'Cow plate 3',
    rotation: [1.2, 2.7, 0],
    width: 608,
    height: 806,
    marginTop: -375,
    marginLeft: -400,
    details: {
      title: 'Cow plate 3',
      year: '2018',
      dimensions: '10" x 10"',
      materials: 'ceramic plate'
    }
  }
]

const drawingsConfig = [
  {
    imgSrc: pog
  },
  {
    imgSrc: pog1
  },
  {
    imgSrc: pog2
  },
  {
    imgSrc: pog3
  }
]

const webpageConfig = [
  {
    href: '#canvasandplexi',
    text: 'Canvas + Plexiglass'
  },
  {
    href: '#plates',
    text: 'Plates'
  },
  {
    href: '#drawings',
    text: 'Drawings'
  },
  {
    href: '#ceramics',
    text: 'Ceramics'
  }
]

const SectionTitle = ({ id, title }) => {
  return (
    <div id={id} style={{ paddingBottom: 5, fontSize: 20, paddingLeft: 5 }}>
      {title}
    </div>
  )
}

const DetailsAndPrice = ({ details, isMobile }) => {
  return (
    <div style={{ width: 580, ...(isMobile ? {} : { display: 'flex', justifyContent: 'space-between' }) }}>
      <div>
        <span style={{ fontStyle: 'italic' }}>{details.title} </span>
        <span>({details.year}), </span>
        <span>{details.dimensions}, </span>
        <span>{details.materials}</span>
      </div>
      {<span>{``}</span>}
      {/* {details.price ? <span>{details.price}</span> : <span>Not for sale</span>} */}
    </div>
  )
}

const ceramicsConfig = [
  {
    glb: bowlGlb,
    usdz: bowlUsdz
  },
  {
    glb: rogerGreenRoundMugGlb,
    usdz: rogerGreenRoundMugUsdz
  },
  {
    glb: b18WarpedMugGlb,
    usdz: b18WarpedMugUsdz
  },
  {
    glb: perfectBowlGlb,
    usdz: perfectBowlUsdz
  },
  {
    glb: pillBowlGlb,
    usdz: pillBowlUsdz
  },
  {
    glb: blueBowlGlb,
    usdz: blueBowlUsdz
  }
]

// In CeramicItem.js
const CeramicItem = ({ ceramic, index }) => {
  const [ref, isVisible] = useViewportVisibility({ threshold: 0.3 })

  const height = '350px'
  // Define width here
  const width = '100%'
  const src = ceramic.glb
  const iosSrc = ceramic.usdz

  return (
    <div
      key={index}
      ref={ref}
      style={{
        display: 'flex',
        justifyContent: 'center',
        width: width // This ensures the container spans the full column
      }}>
      {isVisible ? (
        <SafeModelViewer
          visible={isVisible}
          src={src}
          iosSrc={iosSrc}
          skyboxImage={whiteSkybox}
          skyboxHeight="2m"
          // CRITICAL FIX: Pass the width property here
          style={{ height, width }}
        />
      ) : (
        // Also ensure the placeholder has the correct dimensions
        <div style={{ height, width, background: '#f7f7f7' }} />
      )}
    </div>
  )
}

export const Paintings = () => {
  const size = useScreenSize()
  const isMobile = size.device === deviceMap.mobile

  return (
    <Suspense fallback={<div />}>
      <div style={{ paddingLeft: 5 }}>
        <a href={'/'}>{`\u{1F3E0}`}</a>
      </div>
      <div style={{ paddingBottom: 8, display: 'flex', paddingLeft: 5 }}>
        {webpageConfig.map((w, idx) => {
          const isLast = idx === webpageConfig.length - 1
          return (
            <>
              <div style={{ paddingRight: 5 }}>
                <a href={w.href}>{w.text}</a>
              </div>
              {!isLast && <div style={{ paddingRight: 5 }}>/</div>}
            </>
          )
        })}
      </div>
      <div style={{ paddingBottom: isMobile ? 3 : 18, display: 'flex', paddingLeft: 5, fontStyle: 'italic' }}>
        DM me @darkpano on instagram or by email, jaloeterman at gmail dot com, if you'd like to buy any of these.
      </div>
      {isMobile && (
        <div style={{ paddingBottom: 15, paddingLeft: 5 }}>
          <span style={{ fontStyle: 'italic' }}>{`See 3d scans on a desktop browser `}</span>
          <span>{`\u{1F5A5} \u{1F60E}`}</span>
        </div>
      )}
      <SectionTitle id={'canvasandplexi'} title={'Canvas + Plexiglass'} />
      {paintingConfig.map((painting) => {
        return (
          <div style={{ paddingBottom: 20 }}>
            <DumbImage
              imgSrc={painting.imgSrc}
              glb={painting.glb}
              rotation={painting.rotation}
              width={painting.width}
              height={painting.height}
              marginTop={painting.marginTop}
              marginLeft={painting.marginLeft}
              isMobile={isMobile}
            />
            <div style={{ marginLeft: 10 }}>
              <DetailsAndPrice details={painting.details} isMobile={isMobile} />
            </div>
          </div>
        )
      })}
      <SectionTitle id={'plates'} title={'Plates'} />
      {platesConfig.map((plate) => {
        return (
          <div style={{ paddingBottom: 20 }}>
            <DumbImage
              imgSrc={plate.imgSrc}
              glb={plate.glb}
              rotation={plate.rotation}
              width={plate.width}
              height={plate.height}
              marginTop={plate.marginTop}
              marginLeft={plate.marginLeft}
              isMobile={isMobile}
            />
            <div style={{ marginLeft: 10 }}>
              <DetailsAndPrice details={plate.details} isMobile={isMobile} />
            </div>
          </div>
        )
      })}
      <div style={{ paddingBottom: 15 }}>
        <SectionTitle id={'drawings'} title={'Drawings'} />
        {drawingsConfig.map((drawing) => {
          return <img src={drawing.imgSrc} />
        })}
        <div style={{ marginLeft: 10 }}>
          <span style={{ fontStyle: 'italic' }}>Portraits Over Gchat </span>
          <span>(2019), </span>
          <span>22" x 84", </span>
          <span>graphite on paper, adhered to poster board</span>
        </div>
      </div>
      <SectionTitle id={'ceramics'} title={'Ceramics'} />
      <div style={{ paddingBottom: '20px' }}>
        <div style={{ paddingBottom: '10px' }}>
          <div style={{ padding: '10px', paddingBottom: '0px' }}>Porcelain cone 10 reduction fired.</div>
          <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
            {isMobile
              ? 'In a Safari browser, tap the AR cube to view the piece in your space.'
              : 'Open this page on your iPhone in Safari to see the piece in your space using AR.'}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '1rem',
            // TOP: 1rem, RIGHT: 3rem, BOTTOM: 1rem, LEFT: 3rem
            padding: '1rem'
          }}>
          {ceramicsConfig.map((ceramic, i) => (
            <CeramicItem key={i} ceramic={ceramic} index={i} />
          ))}
        </div>
      </div>
    </Suspense>
  )
}
